/**
 * web-account-pages D6 — save as you go for a form the studio sent, in the
 * Mini App and on the website.
 *
 * The whole answer set is saved about a second after the last change, and at
 * once when the page is hidden, left (`pagehide`) or unmounted, through a
 * `keepalive` request so it outlives the page. A save carries the revision it
 * builds on; a 409 answers the stored revision, and the open page sends what
 * it shows again on that base. A failed save keeps the answers unsaved and is
 * tried again on the next change or when the page comes back. A page that has
 * been left never resends, so it cannot overwrite answers given after
 * reopening. A closed request (410, 404) stops the saving for good. Leaving
 * while the newest answers are on their way in a plain fetch, which the browser
 * may drop with the page, sends them again kept alive on the same base, so one
 * of the two lands (Codex review 2026-10-26; saves that cross on the way are
 * form-draft-save-order).
 *
 * Transport-free: each surface passes its own `send` (the Mini App's `apiFetch`,
 * the website's `webFetch`), so nothing here reads Telegram.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';

export type DraftAnswers = Record<string, unknown>;

/** What one save came back with, in the hook's terms. */
export type DraftOutcome =
  | { readonly kind: 'saved'; readonly revision: number }
  | { readonly kind: 'conflict'; readonly revision: number }
  | { readonly kind: 'closed'; readonly code: string | undefined }
  | { readonly kind: 'failed' };

export interface DraftSave {
  readonly answers: DraftAnswers;
  readonly baseRevision: number;
}

/** Sends one save; `init` carries the body and, when leaving, `keepalive`. Never throws. */
export type DraftSend = (init: RequestInit) => Promise<DraftOutcome>;

/** How long the answers rest after a change before they are saved. */
export const DRAFT_PAUSE_MS = 1000;

/** Browsers refuse a keepalive body over 64 KiB (all in flight together); a signature can pass it. */
const KEEPALIVE_MAX_BYTES = 60_000;

/** A conflict chain this long without a save stops until the next change. */
const MAX_CONFLICT_RESENDS = 3;

/** The request for one save: a POST with the whole set, `keepalive` only when leaving and the body fits. */
export function draftRequestInit(save: DraftSave, leaving: boolean): RequestInit {
  const body = JSON.stringify(save);
  // Over the cap the leaving save goes as a plain fetch: the save a second after the last change already kept a drawn signature, so only edits in the final second before closing are at risk.
  return { method: 'POST', body, ...(leaving && body.length <= KEEPALIVE_MAX_BYTES ? { keepalive: true } : {}) };
}

/** Maps a save's HTTP answer to its outcome (both transports throw on non-2xx with status, code and body). */
export function draftOutcomeOf(status: number, code: string | undefined, body: { revision?: unknown }): DraftOutcome {
  if (status === 409 && typeof body.revision === 'number') return { kind: 'conflict', revision: body.revision };
  if (status === 410 || status === 404 || status === 400) return { kind: 'closed', code };
  return { kind: 'failed' };
}

export interface FormRequestDraftOptions {
  /** The revision the page loaded (bootstrap's `draft.revision`, 0 with no draft); null until loaded. */
  readonly revision: number | null;
  readonly send: DraftSend;
  /** The request closed while the page was open (withdrawn, submitted elsewhere, retired form). */
  readonly onClosed?: (code: string | undefined) => void;
}

export interface FormRequestDraft {
  /** The answers on the page changed; the whole set. */
  readonly change: (answers: DraftAnswers) => void;
  /** Stop saving while a submit is in flight. */
  readonly hold: () => void;
  /** A refused submit: saving resumes, and unsaved answers go now. */
  readonly release: () => void;
}

export function useFormRequestDraft({ revision, send, onClosed }: FormRequestDraftOptions): FormRequestDraft {
  const s = useRef({
    /** The last revision the server confirmed. */
    confirmed: 0,
    loaded: false,
    /** The answers on the page; `changes` counts edits, `saved` the edit last confirmed. */
    answers: null as DraftAnswers | null,
    changes: 0,
    saved: 0,
    /** The newest edit sent in a save still in flight, the base it built on, and whether it was kept alive. */
    sent: 0,
    sentBase: 0,
    sentKeepalive: false,
    inFlight: 0,
    /** A normal save waits for the one in flight. */
    again: false,
    conflicts: 0,
    held: false,
    /** The page was left (pagehide or unmount): no resend. */
    gone: false,
    closed: false,
    timer: null as ReturnType<typeof setTimeout> | null,
  });
  const sendRef = useRef(send);
  sendRef.current = send;
  const onClosedRef = useRef(onClosed);
  onClosedRef.current = onClosed;

  useEffect(() => {
    if (revision === null || s.current.loaded) return;
    s.current.loaded = true;
    s.current.confirmed = revision;
  }, [revision]);

  const flush = useCallback((leaving: boolean) => {
    const st = s.current;
    if (st.timer) clearTimeout(st.timer);
    st.timer = null;
    if (!st.loaded || st.closed || st.held || !st.answers || st.changes === st.saved) return;
    // The newest answers are already on their way; leaving, a plain fetch of them goes again kept alive.
    const resend = st.inFlight > 0 && st.sent === st.changes;
    if (resend && (!leaving || st.sentKeepalive)) return;
    // One normal save at a time; leaving sends now, built on the one in flight landing first.
    if (st.inFlight > 0 && !leaving) {
      st.again = true;
      return;
    }
    const edit = st.changes;
    const baseRevision = resend ? st.sentBase : st.confirmed + st.inFlight;
    const init = draftRequestInit({ answers: st.answers, baseRevision }, leaving);
    st.inFlight += 1;
    st.sent = edit;
    st.sentBase = baseRevision;
    st.sentKeepalive = init.keepalive === true;
    void sendRef.current(init).then((outcome) => {
      st.inFlight -= 1;
      if (outcome.kind === 'saved') {
        st.confirmed = Math.max(st.confirmed, outcome.revision);
        st.saved = Math.max(st.saved, edit);
        st.conflicts = 0;
      } else if (outcome.kind === 'conflict') {
        // Alone, the server's revision is the truth; beside another save, never step back.
        st.confirmed = st.inFlight > 0 ? Math.max(st.confirmed, outcome.revision) : outcome.revision;
        st.conflicts += 1;
        // A page already left lost to a newer save: those answers stand down until its next edit.
        if (st.gone) st.saved = Math.max(st.saved, edit);
        else if (st.conflicts <= MAX_CONFLICT_RESENDS) st.again = true;
      } else if (outcome.kind === 'closed') {
        st.closed = true;
        if (!st.gone) onClosedRef.current?.(outcome.code);
        return;
      } else {
        // Kept unsaved: the next change or the page's return sends them again.
        st.sent = st.saved;
      }
      if (st.again && st.inFlight === 0 && !st.gone) {
        st.again = false;
        flush(false);
      }
    });
  }, []);

  const change = useCallback(
    (answers: DraftAnswers) => {
      const st = s.current;
      st.answers = answers;
      st.changes += 1;
      st.conflicts = 0;
      if (st.timer) clearTimeout(st.timer);
      st.timer = setTimeout(() => flush(false), DRAFT_PAUSE_MS);
    },
    [flush],
  );

  useEffect(() => {
    const st = s.current;
    st.gone = false;
    const onHide = () => {
      st.gone = true;
      flush(true);
    };
    const onShow = () => {
      st.gone = false;
      flush(false);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush(true);
      else flush(false);
    };
    window.addEventListener('pagehide', onHide);
    window.addEventListener('pageshow', onShow);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', onHide);
      window.removeEventListener('pageshow', onShow);
      document.removeEventListener('visibilitychange', onVisibility);
      // Leaving the page inside the app (Back to Home) saves as leaving the tab does.
      st.gone = true;
      flush(true);
    };
  }, [flush]);

  const hold = useCallback(() => {
    const st = s.current;
    st.held = true;
    if (st.timer) clearTimeout(st.timer);
    st.timer = null;
  }, []);

  const release = useCallback(() => {
    s.current.held = false;
    flush(false);
  }, [flush]);

  return useMemo(() => ({ change, hold, release }), [change, hold, release]);
}
